/**
 * @module excel
 *
 * Export to, and import from, MS Excel files.
 */
import XLSX from 'xlsx';
import { nums } from '../tools/nums';
import { validate } from '../tools/validate';
import { noHeaderPrefix } from '../config/settings/attribute.settings';
import { excelTemplateUrl } from '~/config/settings/worktango.settings';

const MAX_SHEETNAME_LENGTH = 31;

/**
 * Convert a value to a "cell" object for use with XLSX package.
 *
 * @param {Any} value - a value to be converted to a XLSX cell object
 * @param {Object} options - configuration options
 * @param {Boolean} options.force - force value to be string (no type casting/conversion)
 * @returns {Object} cell - a cell object for use with XLSX
 * @returns {String} cell.t - cell type (can be one of 's', 'b', 'n', 'd', 'e', or 'z' for string/text, boolean, number, date, error, or stub respectively)
 * @returns {Any} cell.v - cell value (should correspond to cell type)
 *
 * @example cell object:
 * {
 *   t: 's',
 *   v: 'my value'
 * }
 *
 * @see https://www.npmjs.com/package/xlsx
 */
const toCell = (value, options = {}) => {
  // @ts-expect-error TS(2339): Property 'force' does not exist on type '{}'.
  const { force = false } = options;

  /**
   * EMPTY
   */
  if (value === null || value === undefined) {
    return {
      t: 's',
      v: '',
    };
  }

  if (!force) {
    /**
     * BOOLEAN
     *
     * TODO: support real boolean values (e.g., `true` or `false`) using 'b' type instead of 's'.
     * Currently need to use "Yes" and "No" strings  to support legacy logic.
     */
    if (typeof value === 'boolean' || value === 'Yes' || value === 'No') {
      return {
        t: 's',
        v: value === true || value === 'Yes' ? 'Yes' : 'No',
      };
    }

    /**
     * NUMBER
     *
     * TODO: currently disabled to prevent converting values with leadings zeros
     * to a number literal (which strips the leading zeros). Sometimes data
     * intentionally has leading zeros (e.g., "000123") to be used as an id field.
     * Without any way of differentiating between valid numbers and not valid numbers,
     * treating all numeric values as strings atm.
     *
     * To convert to literal number, return something like this:
     *
     * return {
     *   t: 'n',
     *   v: Number(value)
     * };
     */
    if (nums.isNumeric(value)) {
      return {
        t: 's',
        v: String(value),
      };
    }

    /**
     * PERCENT
     *
     * TODO: create a way to handle percentages?
     * example: could be a string that is specifically a number 0-100 followed by '%' literal string?
     */
    // const percentTestValue = Number(value.split('%')[0]);
    // if (percentTestValue >= 0 && percentTestValue <= 100) {
    //   return {
    //     t: 'n',
    //     v: percentTestValue,
    //     z: '%'
    //   };
    // }

    /**
     * To handle postal codes seperately as they were getting confused up
     * by the system as a date object.
     */
    const re = new RegExp('^[^-]+-[^-]+$');

    if (re.test(value)) {
      return {
        t: 's',
        v: String(value),
      };
    }
  }

  /**
   * STRING
   *
   * Everything else is considered a string.
   */
  return {
    t: 's',
    v: String(value),
  };
};

/**
 * Convert Javascript `data` objects to Excel spreadsheet and save to file.
 *
 * @param filename {String} - name of output file
 * @param data {Array} - series of 1 or more data objects for each spreadsheet tab
 * @param data.name {String} - name of tab/sheet
 * @param data.titles {Array} - list of column/title names
 * @param data.rows {Array} - a list of each row of data (each row is itself an array with values corresponding to the titles array)
 * @param options {Object} - optional settings
 * @param options.ext {String} - file extension (default is "xlsx")
 *
 * @example data
 * [
 {
       name: 'Employee List',
       titles: ['my string', 'my bool', 'my num', 'my empty', 'my date'],
       rows: [
         ['string-value', true, 123, null, Date()],
         ['string-value', false, 456, undefined, Date()]
       ]
     },
 {
       name: 'Languages Supported by Employees',
       titles: ['my string', 'my number'],
       rows: [
         ['string-value', 123],
         ['string-value', 456],
         ['string-value', 789]
       ]
     }
 * ]
 *
 * @see https://www.npmjs.com/package/xlsx
 */
const toFile = (filename = '', data = [], options = {}) => {
  // @ts-expect-error TS(2339): Property 'ext' does not exist on type '{}'.
  const { ext = 'xlsx' } = options;
  const workbook = XLSX.utils.book_new();

  data.forEach((tab) => {
    // @ts-expect-error TS(2339): Property 'titles' does not exist on type 'never'.
    const titles = validate.isNonEmptyArray(tab.titles)
      ? // @ts-expect-error TS(2339): Property 'titles' does not exist on type 'never'.
        tab.titles.map((title) => toCell(title, { force: true }))
      : null;
    // @ts-expect-error TS(2339): Property 'rows' does not exist on type 'never'.
    const rows = tab.rows.map((row) => row.map(toCell));
    const sheetData = titles ? [titles, ...rows] : rows;
    const sheet = XLSX.utils.aoa_to_sheet(sheetData);

    // @ts-expect-error TS(2339): Property 'name' does not exist on type 'never'.
    XLSX.utils.book_append_sheet(workbook, sheet, tab.name);
  });

  XLSX.writeFile(workbook, `${filename}.${ext}`);
};

/**
 * Convert a buffer of data to an array of JSON objects.
 *
 * @param buffer {Buffer} - an array of data
 * @param options {Object} - optional configuration settings
 * @param options.rawValues {Boolean} - use raw values (true) or formatted strings (false)
 * @param options.dateFormat {String} - control date value output format
 * @returns {Array} - an array of JSON objects based on input buffer data
 *
 * @see https://www.npmjs.com/package/xlsx#json
 */
const toJson = (buffer, options = {}) => {
  // @ts-expect-error TS(2339): Property 'rawValues' does not exist on type '{}'.
  const { rawValues = false, dateFormat = 'yyyy-mm-dd hh:mm:ss' } = options;
  const data = new Uint8Array(buffer);
  const workbook = XLSX.read(data, {
    type: 'array',
    cellText: true,
    cellDates: true,
  });
  const json = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], {
    raw: rawValues,
    dateNF: dateFormat,
  });

  return json;
};

const getHeadersTitles = (fileData) => {
  const titles = fileData.reduce((headersTitles, row) => {
    const rowsTitles = Object.keys(row);

    headersTitles.push(...rowsTitles);

    return headersTitles;
  }, []);

  return Array.from(new Set(titles));
};

const getUniqueQuestionAnswers = (fileData, header) => {
  const optionsMap = {};

  fileData.forEach((fd) => {
    const answers = fd[header];

    if (answers) {
      const answerArr = answers.split('|||');

      if (answerArr && answerArr.length > 0) {
        answerArr.forEach((answer) => {
          optionsMap[answer] = 1;
        });
      }
    }
  });

  return Object.keys(optionsMap);
};

const buildStringCell = (value) => {
  const parsedValue = String(value || '');

  return { v: parsedValue };
};

const buildPercentageCell = (value, digits = 3) => {
  const parsedValue = Number(value || 0);
  const valueWithDigits = parsedValue.toFixed(digits);
  return {
    z: '0%',
    t: 'n',
    v: valueWithDigits,
  };
};

const buildNumberCell = (value) => {
  const parsedValue = Number(value || 0);

  return {
    t: 'n',
    v: parsedValue,
  };
};

const buildSheetTitles = (titles, macroPadding = null) => {
  const usedTitles = {};

  const sheetTitles = titles.map((title) => {
    const trimmedTitle = title?.trim() ?? '';

    // Remove special char requirement for Excel sheet name
    const parsedTitle = trimmedTitle.replace(/[\\/?*'[\]]/g, ' ');

    // attach pading left required by Excel macros for trends
    const macroTitle = macroPadding ? `${macroPadding} - ${parsedTitle}` : parsedTitle;

    // prevent cell name longer than allowed by Excel
    const allowedTitle = macroTitle.substr(0, MAX_SHEETNAME_LENGTH);

    // Prevent sheet name conflict
    const titleCount = usedTitles[allowedTitle] || 0;
    const newTitleCount = titleCount + 1;
    const hasConflict = newTitleCount > 1;
    const paddingRight = hasConflict ? ` (${newTitleCount})` : '';
    const shortTitle = allowedTitle.substr(0, MAX_SHEETNAME_LENGTH - paddingRight.length);
    const sheetTitle = hasConflict ? `${shortTitle}${paddingRight}` : allowedTitle;

    usedTitles[allowedTitle] = newTitleCount;

    return sheetTitle;
  });

  return sheetTitles;
};

// DEPRECATED: this is too specific (it's making a specific network request).
// Business logic should be inside action creators. This adapter should only
// be concerned with *excel* data (converting data into excel or out of excel).
const downloadByTemplate = (
  templateName,
  fileName,
  sheetTitles,
  sheetRows,
  containVBA = true,
  shouldClearBook = true,
) => {
  const request = new XMLHttpRequest(); // REFACTOR: use http adapter when making network requests

  request.onload = () => {
    if (request.status === 200) {
      const res = new Uint8Array(request.response);
      let workbook;

      if (containVBA) {
        workbook = XLSX.read(res, {
          type: 'array',
          raw: true,
          bookVBA: true,
        });
      } else {
        const bufferArray = [];

        for (let i = 0; i !== res.length; ++i) {
          // @ts-expect-error TS(2322): Type 'string' is not assignable to type 'never'.
          bufferArray[i] = String.fromCharCode(res[i]);
        }

        workbook = XLSX.read(bufferArray.join(''), {
          type: 'binary',
          raw: true,
          bookVBA: true,
        });
      }

      if (shouldClearBook) workbook.SheetNames.pop();

      sheetTitles.forEach((sheetTitle, index) => {
        const sheetRow = sheetRows[index];
        const worksheet = XLSX.utils.aoa_to_sheet(sheetRow);

        XLSX.utils.book_append_sheet(workbook, worksheet, sheetTitle);
      });

      XLSX.writeFile(workbook, `${fileName}.xlsm`);
    }
  };

  // WARNING: mixing concerns here; business logic should not be inside adapters.
  request.open('GET', `${excelTemplateUrl}/${templateName}.xlsm`, true);
  request.responseType = containVBA ? 'arraybuffer' : 'blob';

  request.send();
};

/**
 *
 * @param {*} fileName - Name of the file
 * @param {String[]} sheetTitles - Array of sheet Titles
 * @param {Array[Array[Array[Object]]]} sheetRows - representation of spreadsheet in the form of an arrays
 * The first array represents - Total number of sheets in a workboook.
 * THe second array represents - The number of rows in a sheet.
 * The third array represents - The column data for that particular row.
 *
 * @returns downloaded .xlsx file
 *
 * @example sheetRows - [
 *  Sheet1-Array [
 *    Row1  [
 *      {v: 'Column1'},
 *      {v: 'Column2'},
 *      {v: 'Column3'}
 *    ],
 *    Row2  [
 *      {v: 'Column1'},
 *      {v: 'Column2'},
 *      {v: 'Column3'}
 *    ]
 *  ],
 *  Sheet2-Array [
 *    Row1  [
 *      {v: 'Column1'},
 *      {v: 'Column2'},
 *      {v: 'Column3'}
 *    ]
 *  ]
 * ]
 */
const download = (fileName, sheetTitles, sheetRows) => {
  const workbook = XLSX.utils.book_new();

  sheetTitles.forEach((sheetTitle, index) => {
    const sheetRow = sheetRows[index];
    const worksheet = XLSX.utils.aoa_to_sheet(sheetRow);

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetTitle);
  });

  XLSX.writeFile(workbook, `${fileName}.xlsm`);
};

const employeesToJson = (buffer, options = {}) => {
  // @ts-expect-error TS(2339): Property 'rawValues' does not exist on type '{}'.
  const { rawValues = false, dateFormat = 'yyyy-mm-dd' } = options;
  const data = new Uint8Array(buffer);
  const workbook = XLSX.read(data, {
    type: 'array',
    cellText: true,
    cellDates: true,
    dateNF: dateFormat,
  });
  const json = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], {
    header: 1,
    raw: rawValues,
    dateNF: dateFormat,
  });

  // If table has several columns without headers, as all values will be null or undefined, same name,
  // when convert to json with header as key, then later one will replace previous one if same name
  // Here add prefix '_undefined_' with number to make sure all columns will be kept after converting
  let noHeaderIndex = 0;
  const attributeNames = [];
  for (let i = 0; i < json[0].length; i++) {
    const attributeName =
      json[0][i] === null || json[0][i] === undefined ? `${noHeaderPrefix}${noHeaderIndex++}` : json[0][i];
    attributeNames.push(attributeName);
  }

  let result = [];
  for (let row = 1; row < json.length; row++) {
    // @ts-expect-error TS(2345): Argument of type '{}' is not assignable to paramet... Remove this comment to see the full error message
    result.push({});
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    for (let attributeIndex = 0; attributeIndex < json[0].length; attributeIndex++) {
      // @ts-expect-error TS(2571): Object is of type 'unknown'.
      const attributeName = attributeNames[attributeIndex];
      if (
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        json[row][attributeIndex] ||
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        json[row][attributeIndex] === 0 ||
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        json[row][attributeIndex] === false ||
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        json[row][attributeIndex] === ''
      ) {
        // @ts-expect-error TS(2322): Type 'any' is not assignable to type 'never'.
        result[row - 1][attributeName] = json[row][attributeIndex];
      } else {
        // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'never'.
        result[row - 1][attributeName] = null;
      }
    }
  }

  return result;
};

const employeeIdsToJson = (buffer) => {
  const data = new Uint8Array(buffer);
  const workbook = XLSX.read(data, {
    type: 'array',
    cellText: true,
  });

  return XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);
};

const isCellEmpty = (cell) => {
  return cell === undefined || cell === null || cell === '';
};

const isRowEmpty = (row) => {
  const cellsValues = Object.values(row);
  return cellsValues.every((cell) => isCellEmpty(cell));
};

export {
  toFile,
  toCell,
  toJson,
  getHeadersTitles,
  getUniqueQuestionAnswers,
  buildNumberCell,
  buildPercentageCell,
  buildStringCell,
  buildSheetTitles,
  download,
  downloadByTemplate,
  employeesToJson,
  employeeIdsToJson,
  isRowEmpty,
  isCellEmpty,
};
